import React, { useState, useEffect, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Payment popup modal — renders on top of the InvoiceModal.
 *
 * Collects: Amount paid, Date paid, Payment Method, Reference.
 * On submit, bundles data into the shape expected by
 * paymentService.recordPayment() and calls onSubmit.
 *
 * Props:
 *  - isOpen           – controls visibility
 *  - onClose          – close handler
 *  - onSubmit(data)   – called with payment form data object
 *  - paymentMethods   – rows from payment_methods table
 *  - invoiceTotal     – pre-fills the "Amount paid" field
 */

/* ---- Date helpers (same as InvoiceModal) ---- */
const parseYmd = (ymd) => {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatYmd = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
  );

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      value={value || ''}
      onClick={onClick}
      readOnly
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded cursor-pointer font-[Arial] focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
      <CalendarIcon />
    </span>
  </div>
));

const PopperContainer = ({ children }) => <div className="z-[12000]">{children}</div>;

/* ================================================================ */

export default function PaymentModal({
  isOpen,
  onClose,
  onSubmit,
  paymentMethods = [],
  invoiceTotal = 0,
}) {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [reference, setReference] = useState('');

  // Reset form each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setAmount(invoiceTotal > 0 ? invoiceTotal.toFixed(2) : '');
      setPaymentDate(formatYmd(new Date()));
      setSelectedMethodId('');
      setReference('');
    }
  }, [isOpen, invoiceTotal]);

  if (!isOpen) return null;

  // Look up the full method object for surcharge info
  const selectedMethod = paymentMethods.find(
    (m) => m.id.toString() === selectedMethodId.toString()
  );

  const surchargeRate = parseFloat(selectedMethod?.surcharge_rate || 0);
  const parsedAmount = parseFloat(amount) || 0;
  const surchargeAmount = parsedAmount * surchargeRate;
  const totalWithSurcharge = parsedAmount + surchargeAmount;

  const handleSubmit = () => {
    if (!parsedAmount || parsedAmount <= 0) {
      alert('Please enter a valid payment amount.');
      return;
    }
    if (!selectedMethodId) {
      alert('Please select a payment method.');
      return;
    }

    // Shape matches what paymentService.recordPayment expects
    onSubmit({
      amount: parsedAmount,
      paymentDate,
      paymentMethodId: parseInt(selectedMethodId, 10),
      paymentMethodType: selectedMethod?.payment_type || '',
      surchargeRate,
      reference,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[11000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg font-[Arial]">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">Add Payment</h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 bg-transparent border-none cursor-pointer hover:text-gray-600 leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Row 1: Amount paid + Date paid */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-blue-700 mb-1">
                Amount paid
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Date paid
              </label>
              <DatePicker
                selected={parseYmd(paymentDate)}
                onChange={(d) => setPaymentDate(formatYmd(d))}
                dateFormat="EEE, d MMM yyyy"
                placeholderText="Select date"
                showPopperArrow={false}
                popperContainer={PopperContainer}
                customInput={<DateInput />}
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={10}
                calendarStartDay={1}
                popperClassName="custom-datepicker"
              />
            </div>
          </div>

          {/* Row 2: Payment Method + Reference */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={selectedMethodId}
                onChange={(e) => setSelectedMethodId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select method</option>
                {paymentMethods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.payment_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Reference
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Surcharge + Total summary */}
          {selectedMethod && surchargeRate > 0 && (
            <div className="mb-5 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Surcharge ({(surchargeRate * 100).toFixed(1)}%)</span>
                <span>${surchargeAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-gray-900 mt-1">
                <span>Total</span>
                <span>${totalWithSurcharge.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium bg-white border border-gray-300 text-gray-700 rounded cursor-pointer hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 transition-colors"
            >
              Add Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
